import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { NumberUtils, Tr } from '@utils';
import { ParkingAreaInfrastructure, ParkingTimeSlot } from '@services';
import { CardProps, Grid, IconButton, Stack, Typography, TypographyProps } from '@mui/material';
import { DataTable } from '@components/common';
import { orange, indigo } from '@mui/material/colors';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const Body1: FC<TypographyProps> = ({ sx, ...props }) => <Typography variant="body2" sx={{ ...sx, wordBreak: 'keep-all' }} {...props} />;

export interface ILocationInfrastructureSizingTableProps extends CardProps {
	parkingAreaInfrastructures: ParkingAreaInfrastructure[];
}

export const LocationInfrastructureSizingTable: FC<ILocationInfrastructureSizingTableProps> = ({
	parkingAreaInfrastructures,
	sx,
	...props
}) => {
	const navigate = useNavigate();
	const columns = useMemo<ColumnDef<ParkingAreaInfrastructure>[]>(
		() => [
			{
				accessorKey: 'parkingAreaName',
				header: () => <Tr.Portal path="location.site-infra.parking-area-name" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{row.original.parkingAreaName || ''}</Body1>
			},
			{
				accessorKey: 'numberOfParkingSlots',
				header: () => <Tr.Portal path="location.site-infra.number-of-parking-slots" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{NumberUtils.formatAsNumber(row.original.numberOfParkingSlots, { decimalScale: 0 })}</Body1>
			},
			{
				accessorKey: 'sumOfParkingSlot',
				header: () => <Tr.Portal path="location.site-infra.number-of-evs-to-be-parked" />,
				enableSorting: false,
				cell: ({ row }) => (
					<Grid container direction="row" spacing={1}>
						<Grid item xs={12} sm={12}>
							<Stack direction="row" spacing={1} alignItems="center">
								<LightModeIcon sx={{ width: 20, height: 20, color: orange[600] }} />
								<Body1>
									{NumberUtils.formatAsNumber(row.original.sumOfParkingSlot[ParkingTimeSlot.DAY], { decimalScale: 0 })}
								</Body1>
							</Stack>
						</Grid>
						<Grid item xs={12} sm={12}>
							<Stack direction="row" spacing={1} alignItems="center">
								<DarkModeIcon sx={{ width: 20, height: 20, color: indigo[600] }} />
								<Body1>
									{NumberUtils.formatAsNumber(row.original.sumOfParkingSlot[ParkingTimeSlot.NIGHT], { decimalScale: 0 })}
								</Body1>
							</Stack>
						</Grid>
					</Grid>
				)
			},
			{
				accessorKey: 'acInstalled',
				header: () => <Tr.Portal path="location.site-infra.ac-installed" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{NumberUtils.formatAsNumber(row.original.acInstalled, { decimalScale: 0 })}</Body1>
			},
			{
				accessorKey: 'dcInstalled',
				header: () => <Tr.Portal path="location.site-infra.dc-installed" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{NumberUtils.formatAsNumber(row.original.dcInstalled, { decimalScale: 0 })}</Body1>
			},
			{
				accessorKey: 'acToBeInstalled',
				header: () => <Tr.Portal path="location.site-infra.ac-to-be-installed" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{NumberUtils.formatAsNumber(row.original.acToBeInstalled, { decimalScale: 0 })}</Body1>
			},
			{
				accessorKey: 'dcToBeInstalled',
				header: () => <Tr.Portal path="location.site-infra.dc-to-be-installed" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{NumberUtils.formatAsNumber(row.original.dcToBeInstalled, { decimalScale: 0 })}</Body1>
			},
			{
				accessorKey: 'delta',
				header: () => <Tr.Portal path="location.site-infra.delta-power" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{NumberUtils.formatAsNumber(row.original.delta, { decimalScale: 0, suffix: 'kW/h' })}</Body1>
			},
			{
				accessorKey: 'parkingAreaId',
				header: () => '',
				enableSorting: false,
				size: 0.01,
				cell: ({ row }) => (
					<Stack direction="row" spacing={2}>
						<IconButton
							size="small"
							sx={{ backgroundColor: 'primary.200', color: 'primary.main' }}
							onClick={() => navigate(`./parking-area/${row.original.parkingAreaId}`)}
						>
							<ArrowForwardIcon color="inherit" fontSize="small" />
						</IconButton>
					</Stack>
				)
			}
		],
		[]
	);

	return (
		<DataTable
			columns={columns}
			data={parkingAreaInfrastructures}
			comparator={(d1, d2) => d1.parkingAreaId === d2.parkingAreaId}
			sx={{ ...sx, width: 'unset' }}
			{...props}
		/>
	);
};
